import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Play, Pause, Volume2, VolumeX } from 'lucide-react';
import { PostMedia } from '../../types';

interface MediaCarouselProps {
  media: PostMedia[];
  className?: string;
  showControls?: boolean;
  autoPlay?: boolean;
  onMediaChange?: (index: number) => void;
}

export const MediaCarousel: React.FC<MediaCarouselProps> = ({
  media,
  className = '',
  showControls = true,
  autoPlay = false,
  onMediaChange
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [isMuted, setIsMuted] = useState(true);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  const currentMedia = media[currentIndex];
  const hasMultipleMedia = media.length > 1;

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  useEffect(() => {
    if (onMediaChange) {
      onMediaChange(currentIndex);
    }
  }, [currentIndex, onMediaChange]);

  useEffect(() => {
    // Pause all videos except current one
    videoRefs.current.forEach((video, index) => {
      if (video) {
        if (index === currentIndex && isPlaying && currentMedia?.media_type === 'video') {
          video.play().catch(console.error);
        } else {
          video.pause();
        }
      }
    });
  }, [currentIndex, isPlaying, currentMedia]);

  const goToNext = () => {
    if (hasMultipleMedia) {
      setCurrentIndex((prev) => (prev + 1) % media.length);
    }
  };

  const goToPrevious = () => {
    if (hasMultipleMedia) {
      setCurrentIndex((prev) => (prev - 1 + media.length) % media.length);
    }
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  const togglePlayPause = () => {
    if (currentMedia?.media_type === 'video') {
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
    const currentVideo = videoRefs.current[currentIndex];
    if (currentVideo) {
      currentVideo.muted = !isMuted;
    }
  };

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && hasMultipleMedia) {
      goToNext();
    }
    if (isRightSwipe && hasMultipleMedia) {
      goToPrevious();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      goToPrevious();
    } else if (e.key === 'ArrowRight') {
      goToNext();
    } else if (e.key === ' ') {
      e.preventDefault();
      togglePlayPause();
    }
  };

  if (!media || media.length === 0) {
    return (
      <div className={`relative bg-gray-100 flex items-center justify-center ${className}`}>
        <p className="text-gray-500">No media available</p>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className={`relative bg-gray-100 overflow-hidden ${className}`}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="region"
      aria-label="Media carousel"
    >
      {/* Media Container */}
      <div 
        className="absolute inset-0 flex transition-transform duration-300 ease-in-out"
        style={{ transform: `translateX(-${currentIndex * 100}%)` }}
      >
        {media.map((item, index) => (
          <div key={item.id} className="w-full h-full flex-shrink-0 relative">
            {item.media_type === 'image' ? (
              <img
                src={item.media_url}
                alt={`Media ${index + 1}`}
                className="absolute inset-0 w-full h-full object-cover"
                loading={index === 0 ? 'eager' : 'lazy'}
                onError={(e) => {
                  console.error('Media failed to load:', item.media_url);
                  e.currentTarget.style.display = 'none';
                }}
                onDoubleClick={togglePlayPause}
              />
            ) : (
              <video
                ref={(el) => (videoRefs.current[index] = el)}
                src={item.media_url}
                className="absolute inset-0 w-full h-full object-cover"
                muted={isMuted}
                loop
                playsInline
                preload={index === 0 ? 'metadata' : 'none'}
                onError={(e) => {
                  console.error('Video failed to load:', item.media_url);
                }}
                onClick={togglePlayPause}
              />
            )}
          </div>
        ))}
      </div>

      {/* Navigation Controls */}
      {showControls && hasMultipleMedia && (
        <>
          {/* Previous Button */}
          <button
            onClick={goToPrevious}
            className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white rounded-full p-2 hover:bg-opacity-70 transition-opacity z-10"
            aria-label="Previous media"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>

          {/* Next Button */}
          <button
            onClick={goToNext}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white rounded-full p-2 hover:bg-opacity-70 transition-opacity z-10"
            aria-label="Next media"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </>
      )}

      {/* Video Controls */}
      {currentMedia?.media_type === 'video' && showControls && (
        <div className="absolute bottom-4 left-4 flex space-x-2 z-10">
          <button
            onClick={togglePlayPause}
            className="bg-black bg-opacity-50 text-white rounded-full p-2 hover:bg-opacity-70 transition-opacity"
            aria-label={isPlaying ? 'Pause video' : 'Play video'}
          >
            {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          </button>
          <button
            onClick={toggleMute}
            className="bg-black bg-opacity-50 text-white rounded-full p-2 hover:bg-opacity-70 transition-opacity"
            aria-label={isMuted ? 'Unmute video' : 'Mute video'}
          >
            {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </button>
        </div>
      )}

      {/* Media Type Indicator */}
      {currentMedia?.media_type === 'video' && (
        <div className="absolute top-4 right-4 bg-black bg-opacity-50 text-white rounded-full p-1 z-10">
          <Play className="h-3 w-3" />
        </div>
      )}

      {/* Dots Indicator */}
      {hasMultipleMedia && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 z-10">
          {media.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-2 h-2 rounded-full transition-all duration-200 ${
                index === currentIndex 
                  ? 'bg-white scale-125' 
                  : 'bg-white bg-opacity-50 hover:bg-opacity-75'
              }`}
              aria-label={`Go to media ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Media Counter */}
      {hasMultipleMedia && (
        <div className="absolute top-4 left-4 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded z-10">
          {currentIndex + 1} / {media.length}
        </div>
      )}
    </div>
  );
};